import { Component, computed, inject, input, OnInit, signal, ViewChild } from '@angular/core';
import { AddBankAccountComponent, CreateBankAccountRequest } from './add-bank-account/add-bank-account.component';
import { AsyncPipe, NgIf } from '@angular/common';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatAnchor, MatButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import {
  MatCard,
  MatCardActions,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle,
} from '@angular/material/card';
import { BankAccountListComponent } from './bank-account-list/bank-account-list.component';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BankAccountService } from './bank-account.service';
import { catchError, EMPTY, filter, map, Observable, switchMap, tap } from 'rxjs';
import { Currency, Merchant, PaymentService } from '@galaxy/billing';
import { LoadingStateService } from './loading-state.service';

@Component({
  selector: 'smb-invoicing-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.scss'],
  imports: [
    AsyncPipe,
    NgIf,
    TranslateModule,
    MatAnchor,
    RouterLink,
    MatButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatCardActions,
    GalaxyBadgeModule,
    GalaxyAlertModule,
    GalaxyButtonLoadingIndicatorModule,
    AddBankAccountComponent,
    BankAccountListComponent,
  ],
  providers: [TranslateService],
})
export class BankAccountComponent implements OnInit {
  private bankAccountService = inject(BankAccountService);
  private paymentService = inject(PaymentService);
  private loadingStateService = inject(LoadingStateService);

  merchant = input.required<Merchant>();
  canViewPayouts = input<boolean>(false);
  alwaysDisplaySaveButton = input<boolean>(false);

  bankAccountsLoaded = signal<boolean>(false);
  bankAccounts = toSignal(this.bankAccountService.bankAccounts$.pipe(tap(() => this.bankAccountsLoaded.set(true))));
  hasBankAccountConnected = computed(() => {
    const bankAccounts = this.bankAccounts();
    return bankAccounts && bankAccounts.length > 0;
  });

  retailStatus$ = toObservable(this.merchant).pipe(
    filter(Boolean),
    switchMap((merchant) => this.paymentService.retailStatus(merchant.merchantId)),
    catchError(() => EMPTY),
  );
  retailStatus = toSignal(this.retailStatus$);

  paymentStatus = computed(() => {
    const merchant = this.merchant();
    const retailStatus = this.retailStatus();
    if (merchant?.stripeConnectId && retailStatus?.supportedRegion) {
      return 'payments-configured';
    }
  });

  savingBankAccount = this.bankAccountService.savingBankAccount;
  addBankAccountError = this.bankAccountService.addBankAccountError;

  displayAddBankAccountForm = signal<boolean>(false);
  @ViewChild(AddBankAccountComponent) addBankAccountComponent: AddBankAccountComponent | null = null;
  currency$: Observable<Currency>;

  ngOnInit(): void {
    this.bankAccountService.load(this.merchant()?.merchantId);
    this.currency$ = this.addBankAccountComponent?.currency.valueChanges.pipe(map((currency) => currency));
  }

  addBankAccount(): void {
    this.displayAddBankAccountForm.set(true);
  }

  getAndValidateBankAccount(): CreateBankAccountRequest | null {
    return this.addBankAccountComponent?.validate() ?? null;
  }

  async setDefault(id: string): Promise<void> {
    const merchantId = this.merchant()?.merchantId;
    if (!merchantId) {
      return;
    }
    this.loadingStateService.setLoading(id, true);
    await this.bankAccountService.setDefault(merchantId, id);
    this.loadingStateService.setLoading(id, false);
  }

  async delete(id: string): Promise<void> {
    const merchantId = this.merchant()?.merchantId;
    if (!merchantId) {
      return;
    }
    this.loadingStateService.setLoading(id, true);
    await this.bankAccountService.delete(merchantId, id);
    this.loadingStateService.setLoading(id, false);
  }

  async create(skipSnack?: boolean): Promise<void> {
    const request = this.addBankAccountComponent?.validate() ?? null;
    if (!request) {
      return;
    }
    const merchantId = this.merchant()?.merchantId;
    if (!merchantId) {
      return;
    }
    const success = await this.bankAccountService.create(merchantId, request, skipSnack);
    if (success) {
      this.displayAddBankAccountForm.set(false);
    }
  }

  cancel(): void {
    this.displayAddBankAccountForm.set(false);
  }
}
